import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/ui/Header';
import Footer from '../components/ui/Footer';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowRight, Play, X } from 'lucide-react';

const tabs = [
  { id: 'social-media', label: 'Social Media' },
  { id: 'video-editing', label: 'Video Editing' },
  { id: 'ugc', label: 'User Generated Content' },
];

const portfolioData = {
  'video-editing': [
    { src: 'https://www.youtube.com/embed/mqLiBAhh800', aspect: 'landscape', type: 'youtube' },
    { src: 'https://www.youtube.com/embed/K1BKmnCrZDY', aspect: 'landscape', type: 'youtube' },
    { src: 'https://www.youtube.com/embed/Rypk89DQF4M?start=2', aspect: 'landscape', type: 'youtube' },
    { src: 'https://www.youtube.com/embed/oxiXNBTLVhw', aspect: 'landscape', type: 'youtube' },
    { src: 'https://www.youtube.com/embed/E3JvoqMzwkQ', aspect: 'landscape', type: 'youtube' },
    { src: 'https://www.youtube.com/embed/luCntDwXrbQ?start=16', aspect: 'landscape', type: 'youtube' },
    { src: 'https://www.youtube.com/embed/-O-exPvWdaQ', aspect: 'landscape', type: 'youtube' },
    { src: 'https://www.youtube.com/embed/6NrppyrI3hY', aspect: 'landscape', type: 'youtube' },
  ],
  'ugc': [
    { src: 'https://www.youtube.com/embed/K7PpjYO3CcA', aspect: 'portrait', type: 'youtube' },
    { src: 'https://www.youtube.com/embed/fI5-toYIw-M', aspect: 'portrait', type: 'youtube' },
    { src: 'https://www.youtube.com/embed/gPq_KCW7sAc', aspect: 'portrait', type: 'youtube' },
    { src: 'https://www.youtube.com/embed/2rEqYCfgu-M', aspect: 'portrait', type: 'youtube' },
    { src: 'https://www.youtube.com/embed/uUH5W0QbSfI', aspect: 'portrait', type: 'youtube' },
    { src: 'https://www.youtube.com/embed/mIwt7huXC4Q', aspect: 'portrait', type: 'youtube' },
    { src: 'https://www.youtube.com/embed/QyQd7FFDcB0', aspect: 'portrait', type: 'youtube' },
  ],
  'social-media': [
    { src: 'https://www.youtube.com/embed/vSM0kivw8VI', aspect: 'portrait', type: 'youtube' },
    { src: 'https://www.youtube.com/embed/7T1jGgrFG3c', aspect: 'portrait', type: 'youtube' },
    { src: 'https://www.youtube.com/embed/atdWmggoUM4', aspect: 'portrait', type: 'youtube' },
    { src: 'https://www.youtube.com/embed/I03lYISGK2g', aspect: 'portrait', type: 'youtube' },
    { src: 'https://www.youtube.com/embed/E21iHhLwbFU', aspect: 'portrait', type: 'youtube' },
    { src: 'https://www.youtube.com/embed/zFvuxhKXv2Q', aspect: 'portrait', type: 'youtube' },
    { src: 'https://www.youtube.com/embed/TawmDxNCT-I', aspect: 'portrait', type: 'youtube' },
    { src: 'https://www.youtube.com/embed/iEEPDG-hZM4', aspect: 'portrait', type: 'youtube' },
    { src: 'https://www.youtube.com/embed/aJOONtYHtq0', aspect: 'portrait', type: 'youtube' },
    { src: 'https://www.youtube.com/embed/iUGeMrkEcoo', aspect: 'portrait', type: 'youtube' },
    { src: 'https://www.youtube.com/embed/NFS_vvtH_Yk', aspect: 'portrait', type: 'youtube' },
    { src: 'https://www.youtube.com/embed/lsIBj2Lyq7Y', aspect: 'portrait', type: 'youtube' },
  ],
};

// Helper to extract YouTube video ID from embed URL
const getYouTubeId = (src) => {
  const match = src.match(/\/embed\/([^?]+)/);
  return match ? match[1] : null;
};

const Portfolio = () => {
  const [isVisible, setIsVisible] = useState({});
  const [activeTab, setActiveTab] = useState('ugc');
  const [popupItem, setPopupItem] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('[data-animate]').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen relative z-[1]">
      <Header />
      <div className="pt-32 pb-20">
        <section id="work" className="py-20 relative" data-animate>
          <div className="container mx-auto px-6">
            <div className="text-center mb-20">
              <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/50 mb-4">Portfolio</Badge>
              <h1 className="text-5xl md:text-6xl font-black text-white mb-6">
                Work That <span className="bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">Speaks</span>
              </h1>
              <p className="text-xl text-slate-400 max-w-2xl mx-auto">
                Real projects. Real results. Real impact.
              </p>
            </div>

            {/* Tabs Navigation */}
            <div className="flex flex-wrap justify-center gap-4 mb-16">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-8 py-3 rounded-full font-bold transition-all duration-300 ${
                    activeTab === tab.id
                      ? 'bg-gradient-to-r from-amber-500 to-yellow-400 text-slate-950 shadow-lg shadow-amber-500/30 scale-105'
                      : 'bg-slate-800/50 text-slate-300 border border-slate-700 hover:border-amber-500/50 hover:text-amber-400'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Video Grid */}
            <div className="max-w-7xl mx-auto space-y-10">
              {/* Landscape videos */}
              {portfolioData[activeTab].filter(v => v.aspect === 'landscape').length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
                  {portfolioData[activeTab].filter(v => v.aspect === 'landscape').map((video, index) => {
                    const videoId = video.type === 'youtube' ? getYouTubeId(video.src) : null;
                    const thumbnail = videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : null;
                    return (
                      <div
                        key={`land-${index}`}
                        className="cursor-pointer group"
                        onClick={() => setPopupItem(video)}
                      >
                        <div className="relative aspect-video bg-slate-900/50 border border-slate-800 rounded-2xl overflow-hidden hover:border-amber-500/50 transition-all hover:shadow-lg hover:shadow-amber-500/10">
                          {thumbnail ? (
                            <img
                              src={thumbnail}
                              alt={`Video ${index + 1}`}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          ) : (
                            <iframe
                              src={video.src}
                              className="w-full h-full border-0 pointer-events-none"
                              title={`Video ${index + 1}`}
                              scrolling="no"
                              allowFullScreen
                              style={{ border: 'none', overflow: 'hidden' }}
                            />
                          )}
                          <div className="absolute inset-0 bg-slate-950/30 group-hover:bg-slate-950/10 transition-all flex items-center justify-center">
                            <div className="w-16 h-16 rounded-full bg-amber-500/90 flex items-center justify-center transform group-hover:scale-110 transition-transform shadow-lg shadow-amber-500/30">
                              <Play className="w-8 h-8 text-slate-950 ml-1" />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Portrait videos */}
              {portfolioData[activeTab].filter(v => v.aspect === 'portrait').length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6 max-w-6xl mx-auto">
                  {portfolioData[activeTab].filter(v => v.aspect === 'portrait').map((video, index) => {
                    const videoId = video.type === 'youtube' ? getYouTubeId(video.src) : null;
                    const thumbnail = videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : null;
                    return (
                      <div
                        key={`port-${index}`}
                        className="cursor-pointer group"
                        onClick={() => setPopupItem(video)}
                      >
                        <div className="relative aspect-[9/16] bg-slate-900/50 border border-slate-800 rounded-2xl overflow-hidden hover:border-amber-500/50 transition-all hover:shadow-lg hover:shadow-amber-500/10">
                          {thumbnail ? (
                            <img
                              src={thumbnail}
                              alt={`Video ${index + 1}`}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          ) : (
                            <iframe
                              src={video.src}
                              className="w-full h-full border-0 pointer-events-none"
                              title={`Video ${index + 1}`}
                              scrolling="no"
                              allowFullScreen
                              style={{ border: 'none', overflow: 'hidden' }}
                            />
                          )}
                          <div className="absolute inset-0 bg-slate-950/30 group-hover:bg-slate-950/10 transition-all flex items-center justify-center">
                            <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-amber-500/90 flex items-center justify-center transform group-hover:scale-110 transition-transform shadow-lg shadow-amber-500/30">
                              <Play className="w-6 h-6 md:w-7 md:h-7 text-slate-950 ml-0.5" />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Bottom CTA - Book a Call + Pricing */}
        <section className="py-20 relative">
          <div className="container mx-auto px-6 relative z-10">
            <Card className="bg-slate-900/60 border-amber-500/30 backdrop-blur-sm shadow-2xl shadow-amber-500/10 max-w-4xl mx-auto">
              <CardContent className="p-12 text-center">
                <h2 className="text-4xl font-black text-white mb-4">
                  Let's Build Something <span className="bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">Together</span>
                </h2>
                <p className="text-xl text-slate-400 mb-8">
                  Book a free discovery call and let's discuss how we can bring your vision to life.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/contact">
                    <Button
                      size="lg"
                      className="bg-gradient-to-r from-amber-500 to-yellow-400 text-slate-950 hover:from-amber-400 hover:to-yellow-300 font-bold text-lg px-8 py-6 shadow-2xl shadow-amber-500/40 hover:shadow-amber-500/60 transition-all hover:scale-105">
                      Book a Discovery Call <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                  </Link>
                  <Link to="/editing">
                    <Button
                      size="lg"
                      variant="outline"
                      className="border-2 border-amber-500/50 text-amber-400 hover:bg-amber-500/10 hover:border-amber-400 font-bold text-lg px-8 py-6 transition-all hover:scale-105">
                      Services & Pricing
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      </div>

      {/* Popup Modal for Video */}
      {popupItem && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/90 backdrop-blur-sm p-4"
          onClick={() => setPopupItem(null)}
        >
          <div
            className={`relative bg-slate-900 rounded-2xl border border-amber-500/30 shadow-2xl shadow-amber-500/10 overflow-hidden mx-4 ${
              popupItem.aspect === 'portrait'
                ? 'max-w-sm w-full'
                : 'max-w-4xl w-full'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setPopupItem(null)}
              className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-slate-800/80 text-white hover:bg-amber-500/20 hover:text-amber-400 flex items-center justify-center transition-all"
            >
              <X className="w-5 h-5" />
            </button>
            <div className={`w-full ${
              popupItem.aspect === 'portrait'
                ? 'aspect-[9/16] max-h-[80vh]'
                : 'aspect-video'
            }`}>
              <iframe
                src={popupItem.src}
                className="w-full h-full border-0"
                title="Video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                scrolling="no"
                style={popupItem.type === 'facebook' || popupItem.type === 'gdrive' ? { border: 'none', overflow: 'hidden' } : undefined}
              />
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Portfolio;